import { useEffect, useMemo, useState } from "react";
import useLoggedInUserDetails from "../../../../customHooks/useLoggedInUserDetails";
import {
  LicenseTypes,
  Operation,
  PageFor,
  TableHeaders,
  empType,
} from "../../../../utils/Enum.types";
import {
  EMPTY_STRING,
  ROWS_PER_PAGE,
  emptyMessage,
} from "../../../../utils/constants";
import useUrlParams from "../../../../customHooks/useUrlParams";

import { useNavigate } from "react-router-dom";
import { useLazyQuery, useMutation } from "@apollo/client";
import {
  EmpMasterOrderField,
  EmpQueryType,
  ExportEmpTemplateResult,
  ExportEmpTemplateVars,
  ImportEmpResult,
  ImportEmpVariables,
  ValidateEmpForImportResult,
  ValidateEmpForImportVar,
} from "./queries/types";
import { ImportEmp } from "./queries/mutations";
import {
  ExportEmpTemplate,
  GetEmp,
  ValidateEmpForImport,
} from "./queries/queries";
import { getDownloadUrl } from "../../../../utils/DownloadFile";
import Navbar from "../../../common/layout/Navbar";
import { Title } from "../../../../stories/Title/Title";
import Close from "../../../../images/Close.svg";
import FileAttach from "../../../../images/BrowseFiles.svg";
import TotalStudents from "../../../../images/TotalMale.svg";
import ReadyToImport from "../../../../images/Present.svg";
import ContainsError from "../../../../images/Absent.svg";
import DownArrow from "../../../../images/DownArrow.svg";
import Info from "../../../../images/Information.svg";
import Validate from "../../../../images/Validate.svg";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import ReactModal from "react-modal";
import ExcelLoading from "../../../pages/ExcelLoading";
import MessageModal from "../../../../customComponents/MessageModal";
import ExcelJS from "exceljs";
import * as XLSX from "xlsx";
import { LoadingStyles } from "../../../../styles/ModalStyles";
import { Button } from "../../../../stories/Button/Button";
import { CustomTooltip } from "../../../../styles/TooltipStyles";
import { OrderDirection } from "../../../../utils/commonQueryTypes";
import LoadingModal from "../../../pages/LoadingModal";
import useLabels from "../../../../customHooks/useLabels";
import {
  removeCompleteSpace,
  removeMoreSpace,
} from "../../../../utils/UtilFunctions";

interface Props {
  setModalFlag: React.Dispatch<React.SetStateAction<boolean>>;
  pageType: PageFor;
  NavType: LicenseTypes;
}

const ImportUsers = ({ setModalFlag, NavType, pageType }: Props) => {
  const { user_details } = useLoggedInUserDetails();
  const [records, setRecords] = useState<any[]>([]);
  const [message, setMessage] = useState(emptyMessage);
  const { customer_id } = useUrlParams();
  const [importModal, setImportModal] = useState(false);
  const [successCount, setSuccessCount] = useState(0);
  const [errorCount, setErrorCount] = useState(0);

  const navigate = useNavigate();
  const { companyLabel, subsidiaryLabel, subsidiarySiteLabel } = useLabels();

  const [expanded, setExpanded] = useState(false);

  const [ValidateEachRow] = useLazyQuery<
    ValidateEmpForImportResult,
    ValidateEmpForImportVar
  >(ValidateEmpForImport);

  const [ImportEmployee, { loading: ImportEmpLoading }] = useMutation<
    ImportEmpResult,
    ImportEmpVariables
  >(ImportEmp, {
    onError: (e) => {
      setMessage({
        flag: true,
        message: e.message,
        operation: Operation.NONE,
      });
    },
  });
  const [ExportItems, { loading: ExportLoading }] = useLazyQuery<
    ExportEmpTemplateResult,
    ExportEmpTemplateVars
  >(ExportEmpTemplate, {
    onError: (e) => {
      setMessage({
        flag: true,
        message: e.message,
        operation: Operation.NONE,
      });
    },
  });
  const displayFileName = (): void => {
    const inputElement = document.getElementById("input") as HTMLInputElement;
    const fileName = inputElement.value;
    const fileLabel = document.getElementById("file-name") as HTMLSpanElement;
    fileLabel.innerHTML = fileName.split("\\").pop()!;
  };
  const handleExport = async () => {
    if (customer_id) {
      const { data, errors } = await ExportItems({
        variables: {
          customerID: customer_id,
        },
      });

      if (data && data.ExportEmpTemplate) {
        const res = await getDownloadUrl(data.ExportEmpTemplate, true);
        if (res) {
          setMessage({
            flag: true,
            message: "Downloading...",
            operation: Operation.CREATE,
          });
        }
      } else {
        setMessage({
          flag: true,
          message: "Failed downloading file",
          operation: Operation.NONE,
        });
      }

      if (errors && errors.length > 0) {
        setMessage({
          flag: true,
          message: errors[0].message,
          operation: Operation.NONE,
        });
      }
    }
  };
  const totalRecords = useMemo(() => {
    return records.filter((record) => !record.errors).length;
  }, [records]);

  const downloadExcelContainsError = () => {
    const workbook = new ExcelJS.Workbook();
    const worksheet1 = workbook.addWorksheet("ErrorData");
    worksheet1.views = [{ state: "frozen", xSplit: 1, ySplit: 1 }];
    if (records.length === 0) {
      console.warn("No data available to create the Excel file.");
      return;
    }

    const errorColumns = ["errors"];
    const headers = Object.keys(records[0]).filter(
      (header) =>
        !["isChecked", "isValidated", "isValidatedAndError", "id"].includes(
          header
        )
    );

    errorColumns.forEach((col) => {
      if (!headers.includes(col)) {
        headers.push(col);
      }
    });
    worksheet1.columns = headers.map((header) => ({
      header,
      key: header,
      width: 20,
    }));

    const headerRow = worksheet1.getRow(1);
    headerRow.height = 25;
    headerRow.font = { name: "Arial", size: 12, bold: true };
    headerRow.alignment = { vertical: "middle", horizontal: "center" };
    headerRow.eachCell((cell) => {
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FFFFE0B2" },
      };
      cell.border = {
        top: { style: "thin" },
        left: { style: "thin" },
        bottom: { style: "thin" },
        right: { style: "thin" },
      };
    });

    records
      .filter((record) => record?.errors)
      .forEach((entry, rowIndex) => {
        const row = worksheet1.getRow(rowIndex + 2);
        headers.forEach((header, colIndex) => {
          const cell = row.getCell(colIndex + 1);
          cell.value = entry[header];
          cell.border = {
            top: { style: "thin" },
            left: { style: "thin" },
            bottom: { style: "thin" },
            right: { style: "thin" },
          };
          cell.alignment = { vertical: "middle", horizontal: "center" };
        });
      });

    workbook.xlsx.writeBuffer().then((buffer) => {
      const blob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "StudentInfo.xlsx");
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    });
  };

  const handleAddItems = async () => {
    setSuccessCount(0);
    setErrorCount(0);
    setImportModal(true);

    for (const record of records.filter((record) => !record.errors)) {
      try {
        await ImportEmployee({
          variables: {
            user_details,
            customerID: customer_id!,
            input: {
              name: removeMoreSpace(record.Name),
              companyName: removeMoreSpace(
                record[`${removeCompleteSpace(companyLabel)}`]
              ),
              subsidiaryName: removeMoreSpace(
                record[`${removeCompleteSpace(subsidiaryLabel)}`]
              ),
              siteName: removeMoreSpace(
                record[`${removeCompleteSpace(subsidiarySiteLabel)}`]
              ),
              mobileNo: `+91${record.Mobile}`,
              kgid: record.KGID,
              metalNo: record.MetalNo,
              empType: removeMoreSpace(record.Type) as empType,
            },
          },
          refetchQueries: [
            {
              query: GetEmp,
              variables: {
                after: null,
                customer_id,
                first: ROWS_PER_PAGE,
                input: {
                  queryType: EmpQueryType.ALL,
                  empType: [empType.DCP],
                  companyID: 0,
                  ldgrID: 0,
                  siteID: 0,
                  subsidiaryID: 0,
                },
                orderBy: {
                  direction: OrderDirection.ASC,
                  field: EmpMasterOrderField.NAME,
                },

                name: EMPTY_STRING,
              },
            },
          ],
        }).then(({ data }) => {
          if (data) {
            setSuccessCount((prev) => prev + 1);
          }
        });
      } catch (error) {
        console.error(error);
        setErrorCount((prev) => prev + 1);
      }
    }
  };

  const handleClose = () => {
    if (message.operation !== Operation.NONE) {
      setModalFlag(false);
    }
    setMessage({
      message: "",
      flag: false,
      operation: Operation.NONE,
    });
  };
  const user_schema = {
    Name: {
      Headers: "Name",
      key: "Name",
      type: "String",
      required: false,
      use_in_sheet: true,
      width: 50,
    },
    [`${removeCompleteSpace(companyLabel)}`]: {
      Headers: "Company Name",
      key: "Company",
      type: "String",
      required: true,
      use_in_sheet: true,
      width: 50,
    },
    [`${removeCompleteSpace(subsidiaryLabel)}`]: {
      Headers: "Subsidiary Name",
      key: "Subsidiary",
      type: "String",
      required: false,
      use_in_sheet: true,
      width: 50,
    },
    [`${removeCompleteSpace(subsidiarySiteLabel)}`]: {
      Headers: "Site Name",
      key: "Site",
      type: "String",
      required: false,
      use_in_sheet: true,
      width: 50,
    },
    Mobile: {
      Headers: "Mobile No",
      key: "Mobile",
      type: "String",
      required: true,
      use_in_sheet: true,
      width: 50,
    },
    KGID: {
      Headers: "KGID",
      key: "KGID",
      type: "Int",
      required: true,
      use_in_sheet: true,
      width: 50,
    },
    MetalNo: {
      Headers: "Metal No",
      key: "MetalNo",
      type: "Int",
      required: false,
      use_in_sheet: true,
      width: 50,
    },
    Type: {
      Headers: "Type",
      key: "Type",
      type: "EmpType",
      required: true,
      use_in_sheet: true,
      width: 50,
    },
  };

  const schemaKeys = Object.keys(user_schema);

  const processExcel = (data: any) => {
    const workbook = XLSX.read(data, { type: "binary" });
    const firstSheet = workbook.SheetNames[0];
    const records = XLSX.utils.sheet_to_json(workbook.Sheets[firstSheet]);
    records.forEach((record: any) => {
      let errors: any = [];

      Object.keys(user_schema).forEach((key) => {
        // @ts-ignore
        let keySchema = user_schema[key];
        let keyValue = record[key];

        if (
          keySchema?.required &&
          (keyValue === null || keyValue === undefined)
        ) {
          let errorObj = {
            column: key,
            error: "Required",
          };
          errors.push(errorObj);
        }
      });

      if (errors.length > 0) {
        record.errors = errors;
      }
    });

    return records;
  };

  useEffect(() => {
    const input = document.getElementById("input") as HTMLInputElement;
    if (input) {
      input.addEventListener("change", () => {
        const reader = new FileReader();
        reader.onload = (e) => {
          const res = processExcel(reader.result);
          setRecords(
            res.map((f: any, index: number) => {
              return {
                ...f,
                Name: f.Name,
                [`${removeCompleteSpace(companyLabel)}`]:
                  f[`${removeCompleteSpace(companyLabel)}`],
                [`${removeCompleteSpace(subsidiaryLabel)}`]:
                  f[`${removeCompleteSpace(subsidiaryLabel)}`],
                [`${removeCompleteSpace(subsidiarySiteLabel)}`]:
                  f[`${removeCompleteSpace(subsidiarySiteLabel)}`],
                Mobile: f.Mobile,
                KGID: f.KGID,
                MetalNo: f.MetalNo,
                Type: f.Type,
                id: index + 1,
                isChecked: false,
                isValidated: false,
                isValidatedAndError: false,
              };
            })
          );
        };
        reader.readAsBinaryString(input.files![0]);
      });
    }
  });

  const handleButtonClick = async () => {
    try {
      await Promise.all(records.map((record) => handleClick(record.id)));
    } catch (error) {
      console.error(error);
    }
  };

  const handleClick = async (id: number) => {
    try {
      const filteredData = records.find((record) => record.id === id);

      if (filteredData) {
        const { data, errors } = await ValidateEachRow({
          variables: {
            customerID: customer_id!,

            input: {
              name: removeMoreSpace(filteredData.Name),
              companyName: removeMoreSpace(
                filteredData[`${removeCompleteSpace(companyLabel)}`]
              ),
              subsidiaryName: removeMoreSpace(
                filteredData[`${removeCompleteSpace(subsidiaryLabel)}`]
              ),
              siteName: removeMoreSpace(
                filteredData[`${removeCompleteSpace(subsidiarySiteLabel)}`]
              ),
              mobileNo: removeMoreSpace(filteredData.Mobile),
              kgid: filteredData.KGID,
              metalNo: filteredData.MetalNo,
              empType: filteredData.Type,
            },
          },
        });

        if (data) {
          const newData = records.map((record) => {
            if (id === record.id) {
              return {
                ...record,
                isChecked: !record?.isChecked,
                isValidated: !record.isValidated,
                isValidatedAndError: false,
              };
            }
            return record;
          });
          setRecords(newData);
          return Promise.resolve(data);
        }

        if (errors && errors.length > 0) {
          const newData = records.map((record) => {
            if (record.id === id) {
              record.errors = record.errors ? record.errors : [];
              record.errors.push(errors[0]?.message);
              return {
                ...record,
                isChecked: !record?.isChecked,
                isValidated: true,
                isValidatedAndError: true,
              };
            }
            return record;
          });
          setRecords(newData);
          return Promise.reject(errors[0]);
        }
      }
    } catch (err) {
      setMessage({
        flag: true,
        message: (err as Error).message || "An error occurred",
        operation: Operation.NONE,
      });
      return Promise.reject(err);
    }
  };

  return (
    <>
      {pageType === PageFor.GENERAL ? (
        <>
          <Navbar NavType={NavType} />
          <Title>Import Users</Title>
        </>
      ) : null}
      <div
        className={`import-excel-data${
          pageType === PageFor.GENERAL ? "" : "__modal"
        }`}
      >
        {pageType === PageFor.MODAL && (
          <div className="import-excel-data--title">
            <Title>Import Users</Title>
            <img src={Close} alt="" onClick={() => setModalFlag(false)} />
          </div>
        )}
        <div className="row g-0 import-excel-data__blocks">
          <div className="col-6">
            <div className="import-excel-data__file-upload">
              <div className="import-excel-data__file-upload--file-name">
                <span id="file-name"></span>
              </div>
              <div className="import-excel-data__file-upload--browse">
                <label htmlFor="input">
                  <img src={FileAttach} alt="/" /> Choose Excel File
                </label>
                <input
                  id="input"
                  type="file"
                  name="file"
                  onChange={() => displayFileName()}
                  className="import-excel-data__file-upload--input"
                />
              </div>
            </div>
          </div>

          <div className="col import-excel-data__blocks--cards">
            <b>
              <img src={TotalStudents} alt="/" />
              Total Users
            </b>
            <span className="import-excel-data__blocks--cards--total-students">
              {records.length}
            </span>
          </div>
          <div
            className="col import-excel-data__blocks--cards"
            // onClick={() => setExpanded(!expanded)}
          >
            <b>
              <img src={ReadyToImport} alt="/" />
              Ready to Import
            </b>
            <span className="import-excel-data__blocks--cards--ready-to-import">
              {records.filter((record) => !record?.errors).length}
            </span>
          </div>
          <div
            className="col import-excel-data__blocks--cards"
            onClick={() => setExpanded(!expanded)}
          >
            <b>
              <img src={ContainsError} alt="/" />
              Contains Error
            </b>
            <span className="import-excel-data__blocks--cards--contains-error">
              {records.filter((record) => record?.errors).length}
            </span>
          </div>
        </div>
        <div className="import-excel-data__datablock">
          {records.length > 0 ? (
            <>
              <div
                className="import-excel-data__datablock--title"
                onClick={() => setExpanded(!expanded)}
              >
                <div className="import-excel-data__datablock--records-count">
                  <span>
                    {records.filter((record) => !record.errors).length}
                  </span>
                  <b> Records Found to Import</b>
                </div>
                <img src={DownArrow} alt="/" />
              </div>
              <div className="import-excel-data__datablock--tableblock">
                <TableContainer className="import-excel-data__datablock--table">
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell id="td-center">
                          {TableHeaders.SLNO}
                        </TableCell>
                        {schemaKeys
                          .filter((key) =>
                            records.some((record) => key in record)
                          )
                          .map((key, index) => (
                            <TableCell key={index}>{key}</TableCell>
                          ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {records
                        .filter((record) => !record?.errors)
                        .map((record, index) => (
                          <TableRow key={index}>
                            <TableCell id="td-center">{index + 1}</TableCell>
                            {schemaKeys
                              .filter((key) => key in record)
                              .map((key, colIndex) => (
                                <TableCell key={colIndex}>
                                  {record[key]}
                                </TableCell>
                              ))}
                          </TableRow>
                        ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </div>
              <div
                className="import-excel-data__datablock--title"
                onClick={() => setExpanded(!expanded)}
              >
                <div className="import-excel-data__datablock--errors-count">
                  <span>{records.filter((d) => d?.errors).length}</span>
                  <b>Records Which Contains error(s)</b>
                </div>
                <img src={DownArrow} alt="/" />
              </div>
              {expanded && (
                <div className="import-excel-data__datablock--tableblock">
                  <TableContainer className="import-excel-data__datablock--table">
                    <Table stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell id="td-center">
                            {TableHeaders.SLNO}
                          </TableCell>
                          {schemaKeys
                            .filter((key) =>
                              records.some((record) => key in record)
                            )
                            .map((key, index) => (
                              <TableCell key={index}>{key}</TableCell>
                            ))}
                          <TableCell id="td-center">Error Details</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {records
                          .filter((record) => record?.errors)
                          .map((record, index) => {
                            return (
                              <TableRow key={index}>
                                <TableCell id="td-center">
                                  {index + 1}
                                </TableCell>
                                {schemaKeys
                                  .filter((key) => key in record)
                                  .map((key, colIndex) => (
                                    <TableCell key={colIndex}>
                                      {record[key]}
                                    </TableCell>
                                  ))}

                                <TableCell className="import-excel-data__datablock--error">
                                  <CustomTooltip
                                    title={
                                      <ul>
                                        {record?.errors
                                          ? record?.errors?.map(
                                              (error: any, index: number) => {
                                                return (
                                                  <li key={index}>
                                                    {index + 1})&nbsp;
                                                    {typeof error === "string"
                                                      ? error
                                                      : "Error message = " +
                                                        error.error +
                                                        ", column = " +
                                                        error.column}
                                                  </li>
                                                );
                                              }
                                            )
                                          : null}
                                      </ul>
                                    }
                                    arrow
                                    placement="bottom-start"
                                  >
                                    <div className="import-excel-data__datablock--tableblock--error-message">
                                      <span>Errors</span>
                                      <img src={Info} alt="/" />
                                    </div>
                                  </CustomTooltip>
                                </TableCell>
                              </TableRow>
                            );
                          })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </div>
              )}
            </>
          ) : null}
        </div>
        <div className="row g-0">
          <div className="col">
            <Button
              onClick={() => {
                setImportModal(true);
                handleAddItems();
              }}
              disabled={!records.length ? true : false}
              mode="excel"
            >
              Import Users
            </Button>

            <Button onClick={handleButtonClick}>
              <img src={Validate} alt="" />
              Validate
            </Button>

            <Button onClick={downloadExcelContainsError} mode="excel">
              Export error Data
            </Button>
            <Button
              mode={pageType === PageFor.GENERAL ? "back" : "cancel"}
              onClick={() => {
                if (pageType === PageFor.MODAL) {
                  setModalFlag(false);
                } else {
                  navigate(-1);
                }
              }}
            />
          </div>
          <div className="col import-excel-data__button">
            <Button mode="excel" onClick={handleExport}>
              Generate Template XLS File
            </Button>
          </div>
        </div>
      </div>

      <ReactModal
        shouldCloseOnOverlayClick={true}
        isOpen={importModal}
        style={LoadingStyles}
        ariaHideApp={false}
        onRequestClose={() => setImportModal(!importModal)}
      >
        <ExcelLoading
          total={totalRecords}
          success={successCount}
          errors={errorCount}
          loading={false}
          setModalFlag={setImportModal}
        />
      </ReactModal>
      <LoadingModal flag={ImportEmpLoading || ExportLoading} />
      <MessageModal
        modalFlag={message.flag!}
        value={message.message!}
        handleClose={handleClose}
        operation={message.operation!}
      />
    </>
  );
};

export default ImportUsers;
